// src/components/heatmap/LeafletHeatLayer.tsx
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import type { Layer } from "leaflet";
import "leaflet.heat";

type TuplePoint = [number, number, number?];
type ObjPoint = { lat: number; lng: number; intensity?: number };
export type HeatPoint = TuplePoint | ObjPoint;

type Props = {
  points: HeatPoint[];
  radius?: number;
  blur?: number;
  maxZoom?: number;
  max?: number;
};

const GRADIENT: Record<number, string> = {
  0.0: "rgba(254,240,138,0.15)", // amber-200
  0.4: "rgba(251,146,60,0.55)",  // orange-400
  0.7: "rgba(239,68,68,0.85)",   // red-500
  1.0: "rgba(185,28,28,0.95)",   // red-700
};

function toTriplets(src: HeatPoint[]): [number, number, number][] {
  const out: [number, number, number][] = [];
  for (const p of src || []) {
    if (!p) continue;
    if (Array.isArray(p)) {
      const [lat, lng, w] = p;
      if (typeof lat === "number" && typeof lng === "number") {
        out.push([lat, lng, typeof w === "number" ? w : 0.6]);
      }
    } else {
      const lat = (p as ObjPoint).lat;
      const lng = (p as ObjPoint).lng;
      if (typeof lat === "number" && typeof lng === "number") {
        out.push([lat, lng, typeof p.intensity === "number" ? p.intensity : 0.6]);
      }
    }
  }
  return out;
}

export function LeafletHeatLayer({
  points,
  radius = 25,
  blur = 20,
  maxZoom = 18,
  max = 1,
}: Props) {
  const map = useMap();
  const layerRef = useRef<Layer & { setLatLngs?: Function } | null>(null);
  const cacheRef = useRef<string>("");

  useEffect(() => {
    const L: any = (window as any).L;
    if (!L || !map) return;
    if (!layerRef.current) {
      // @ts-ignore
      layerRef.current = (L as any).heatLayer([], {
        radius,
        blur,
        maxZoom,
        max,
        gradient: GRADIENT,
      });
      layerRef.current.addTo(map);
    }
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, radius, blur, maxZoom, max]);

  useEffect(() => {
    if (!layerRef.current) return;
    const triplets = toTriplets(points);
    const key = triplets.length
      ? triplets.slice(0, 50).map((t) => t.join(",")).join("|")
      : "empty";
    if (key === cacheRef.current) return;
    cacheRef.current = key;
    // @ts-ignore
    layerRef.current.setLatLngs(triplets);
  }, [points]);

  return null;
}

export default LeafletHeatLayer;

